// ///////////////////////////////////////////////////////////
// tbSpine: The engine part dealing with spine animations
//
// Copyright 2016 CoolGames
// Last Changed: 22-3-2016
// ///////////////////////////////////////////////////////////
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { BaseLoader } from "../code/asset_loading/BaseLoader";
import { LoadManager } from "../code/asset_loading/LoadManager";
import { tgfGraphics } from "../framework/tgfGraphics";
import { ErrorLogger } from "../framework/ErrorLogger";
import { tbBase } from "./tbBase";
import { getPIXIObjectFromTbObject } from "./tbGetPIXIObject";
import { PIXI } from "./tbPIXI";
import { tbLoaderManager } from "./tbLoaderManager";
import { tbLoader } from "./tbLoader";
// Note that we reference the various methods and properties in the spine runtime
// by string, to avoid problems with obfuscation. The spine runtime cannot be obfuscated.
var tbSpine = /** @class */ (function (_super) {
    __extends(tbSpine, _super);
    function tbSpine(name, texture, filePath, spineParent) {
        var _this = _super.call(this) || this;
        _this.skeleton = null;
        _this.skeletonData = null;
        _this.state = null;
        _this.stateDate = null;
        _this.didLoad = false;
        _this.speed = 1;
        _this.scale = 1;
        _this._onComplete = null;
        _this._events = [];
        _this.name = name;
        _this.texture = texture;
        _this.filePath = filePath;
        _this.spineParent = spineParent;
        _this.x = 0;
        _this.y = 0;
        _this.rotation = 0;
        _this.alpha = 1;
        if (spineParent === undefined) {
            tbLoader.addResource(_this, "spine", 1);
            tbLoaderManager.addSpine();
        }
        return _this;
    }
    // Sets the namespace (such that we can change the visuals without redoing changing the animation)
    tbSpine.prototype.setPosition = function (x, y) {
        this.x = x;
        this.y = y;
        if (this.animation) {
            this.animation["x"] = this.x;
            this.animation["y"] = this.y;
        }
    };
    tbSpine.prototype.setAlpha = function (alpha) {
        this.alpha = Math.min(Math.max(alpha, 0), 1);
        this.animation["alpha"] = this.alpha;
    };
    tbSpine.prototype.setRotation = function (rotation) {
        if (this.animation) {
            this.animation["rotation"] = rotation;
            this.rotation = rotation;
        }
    };
    tbSpine.prototype.setScale = function (scale) {
        this.scale = scale;
        if (this.animation) {
            this.animation["scale"]["set"](scale);
        }
    };
    tbSpine.prototype.flip = function () {
        if (this.animation) {
            this.animation["scale"]["x"] = (this.scale * -1);
        }
    };
    tbSpine.prototype.flipVertically = function () {
        if (this.animation) {
            this.animation["scale"]["y"] = (this.scale * -1);
        }
    };
    tbSpine.prototype.show = function () {
        if (this.animation) {
            this.animation["visible"] = true;
        }
    };
    tbSpine.prototype.hide = function () {
        if (this.animation) {
            this.animation["visible"] = false;
        }
    };
    tbSpine.prototype.addChild = function (child) {
        if (!this.animation) {
            return;
        }
        child = getPIXIObjectFromTbObject(child);
        if (child) {
            this.animation["addChild"](child);
        }
    };
    tbSpine.prototype.add = function (container) {
        if (this.animation) {
            if (container && container["addChild"]) {
                container["addChild"](this.animation);
            }
            else {
                tgfGraphics.addChild(this.animation);
            }
        }
    };
    tbSpine.prototype.removeChild = function (child) {
        if (!this.animation) {
            return;
        }
        child = getPIXIObjectFromTbObject(child);
        if (child) {
            this.animation["removeChild"](child);
        }
    };
    tbSpine.prototype.remove = function (container) {
        if (this.animation) {
            if (container && container["removeChild"]) {
                container["removeChild"](this.animation);
            }
            else {
                tgfGraphics.removeChild(this.animation);
            }
        }
    };
    tbSpine.prototype.free = function (alsoChildren) {
        if (alsoChildren === void 0) { alsoChildren = true; }
        var options = { "children": alsoChildren,
            "texture": false,
            "baseTexture": false };
        if (this.animation) {
            this.animation["destroy"](options);
            this.animation = null;
        }
    };
    tbSpine.prototype.setTint = function (tint) {
        if (this.animation) {
            this.animation["tint"] = tint;
        }
    };
    tbSpine.prototype.setMask = function (mask) {
        if (this.animation) {
            this.animation["mask"] = mask;
        }
    };
    // *****************************
    // Dealing with animations
    // *****************************
    // Sets the animation to play
    tbSpine.prototype.setAnimation = function (animation, loop) {
        if (this.animation && this.animation["state"]["hasAnimation"](animation)) {
            this.animation["state"]["setAnimation"](0, animation, loop);
            // this.animation["skeleton"]["setToSetupPose"]();
            // this.animation["skeleton"]["setSlotsToSetupPose"]();
        }
    };
    tbSpine.prototype.setSkin = function (skin) {
        this.animation["skeleton"]["setSkinByName"](skin);
    };
    tbSpine.prototype.addAnimation = function (animation, loop) {
        if (this.animation && this.animation["state"]["hasAnimation"](animation)) {
            this.animation["state"]["addAnimation"](0, animation, loop);
            this.animation["skeleton"]["setToSetupPose"]();
        }
    };
    // *****************************
    // Setting properties
    // *****************************
    /** Sets the speed with which to play (1 is default, must be >= 0) */
    tbSpine.prototype.setSpeed = function (speed) {
        if (this.animation && speed) {
            this.animation["state"]["timeScale"] = speed;
        }
    };
    /**
     * @returns The speed with which to play
     */
    tbSpine.prototype.getSpeed = function () {
        if (this.animation) {
            this.speed = this.animation["state"]["timeScale"];
        }
        return this.speed;
    };
    tbSpine.prototype.getAnimation = function () {
        return this.animation;
    };
    // *****************************
    // Dealing with events
    // *****************************
    // Sets an event to call a particular callback
    tbSpine.prototype.setEventCallback = function (event, callback, context) {
        // Check whether it already exists
        for (var i = this._events.length - 1; i >= 0; i -= 1) {
            if (this._events[i].event === event) {
                this._events[i].callback = callback;
                this._events[i].context = context;
                return;
            }
        }
        this._events.push({ event: event, callback: callback, context: context });
    };
    tbSpine.prototype.getEventCallback = function (event) {
        var _callback;
        for (var i = this._events.length - 1; i >= 0; i -= 1) {
            if (this._events[i].event === event) {
                _callback = this._events[i].callback;
            }
        }
        return _callback;
    };
    tbSpine.prototype.clearEventCallback = function (event) {
        for (var i = this._events.length - 1; i >= 0; i -= 1) {
            if (this._events[i].event === event) {
                this._events.splice(i, 1);
            }
        }
    };
    /** Clear all event callbacks */
    tbSpine.prototype.clearAllEventCallbacks = function () {
        this._events = [];
    };
    /** Sets the callback to be called on complete */
    tbSpine.prototype.setOnComplete = function (callback) {
        this._onComplete = callback;
    };
    /**
     * @returns The callback on complete
     */
    tbSpine.prototype.getOnComplete = function () {
        return this._onComplete;
    };
    /** Clears the callback on complete */
    tbSpine.prototype.clearOnComplete = function () {
        this._onComplete = null;
    };
    /** Initialize spines callback functions */
    tbSpine.prototype._initEvents = function () {
        var _this = this;
        if (this.animation["state"]) {
            this.animation["state"]["onEvent"] = function (trackIndex, event) {
                var i;
                for (i = 0; i < _this._events.length; i += 1) {
                    if (_this._events[i].event === event["data"]["name"]) {
                        if (_this._events[i].callback) {
                            _this._events[i].callback(_this._events[i].context);
                        }
                    }
                }
            };
            this.animation["state"]["onComplete"] = function (trackIndex, count) {
                if (_this._onComplete) {
                    _this._onComplete();
                }
            };
        }
    };
    // *****************************
    // Internal Functions
    // *****************************
    tbSpine.prototype.startLoading = function (moment, location) {
        var _this = this;
        // Sometimes the load moment is "spine" and sometimes it's ["spine"]
        if (Array.isArray(moment)) {
            moment.forEach(function (m) { return _this.startLoading(m); });
            return;
        }
        LoadManager.logLoadInfo("tbSpine::startLoading", this.name, moment);
        if (tbLoader.complete(moment)) {
            return Promise.resolve();
        }
        if (this._loadPromise) {
            return this._loadPromise;
        }
        if (this.baseLoader === undefined) {
            this.baseLoader = new BaseLoader();
        }
        if (this.texture === undefined) {
            LoadManager.logLoadInfo("Spine has an undefined texture", this.name, moment);
            return Promise.reject("Spine has an undefined texture");
        }
        this._loadPromise = new Promise(function (resolve, reject) {
            _this.texture.startLoading().then(function () {
                var retryAttempts = 1;
                if (!_this.texture.didLoad) {
                    console.error("Tried to load spine " + _this.name + " before its texture was loaded");
                    reject("Tried to load spine " + _this.name + " before its texture was loaded");
                    return;
                }
                // Making sure the texure were using is a valid one
                for (var i = 0; i < _this.texture.filenames.length; i++) {
                    var imageResource = _this.texture.baseLoader.getResource(_this.texture.filenames[i] + "_image");
                    if (imageResource === undefined || imageResource.texture === undefined ||
                        !imageResource.texture.valid || imageResource.texture.baseTexture === undefined ||
                        !imageResource.texture.baseTexture.hasLoaded ||
                        (imageResource.texture.baseTexture.width <= 1 && imageResource.texture.baseTexture.height <= 1)) {
                        reject("Invalid texture used for spine animation " + _this.texture.filenames[i]);
                        return;
                    }
                }
                _this._atlas = _this._createAtlas();
                if (_this._atlas === undefined) {
                    reject("No atlas images found for texture");
                    return;
                }
                var assetInfo = {
                    name: _this.name,
                    url: _this.filePath,
                    metadata: { "spineAtlas": _this._atlas }
                };
                _this.baseLoader.prepareAsset(assetInfo);
                var onError = function (error) { return reject(error); };
                var onLoadDone = function (resources) {
                    var errorMessage = null;
                    var resource = resources[_this.name];
                    if (resource === undefined) {
                        errorMessage = "Failed to get spine resource " + _this.name;
                    }
                    else if (resource.error != null) {
                        errorMessage = resource.error;
                    }
                    else if (resource.spineData === undefined) {
                        errorMessage = "SpineData of " + _this.name + " is undefined";
                    }
                    if (errorMessage !== null) {
                        console.error(errorMessage, _this);
                        if (retryAttempts < LoadManager.loadRetryMax) {
                            LoadManager.logLoadInfo(errorMessage + "::retrying to load spine again", retryAttempts);
                            retryAttempts++;
                            _this.baseLoader.reset();
                            setTimeout(_this.baseLoader.loadAsset.bind(_this.baseLoader), LoadManager.loadRetryDelay, onLoadDone, onError);
                        }
                        else {
                            reject(errorMessage);
                        }
                    }
                    else {
                        LoadManager.logLoadInfo("Load Spine Asset::SUCCESS", _this.name);
                        _this.spineData = resource.spineData;
                        _this.animation = new PIXI.spine.Spine(_this.spineData);
                        _this._initEvents();
                        tbLoaderManager.loadedSpine();
                        resolve();
                    }
                };
                LoadManager.logLoadInfo("Load Spine Asset", _this.filePath, _this.name, moment);
                _this.baseLoader.loadAsset(onLoadDone, onError);
            }, function (reason) {
                ErrorLogger.sendError(reason);
                reject(reason);
            });
        });
        return this._loadPromise;
    };
    tbSpine.prototype._createAtlas = function () {
        var atlas = new PIXI.spine.core.TextureAtlas();
        for (var i = 0; i < this.texture.filenames.length; i++) {
            var atlasImages = this.texture.getAtlasImages(this.texture.filenames[i]);
            if (atlasImages === undefined) {
                return undefined;
            }
            atlas.addTextureHash(atlasImages, true);
        }
        return atlas;
    };
    /**
     * @returns Whether the texture is loaded or not
     */
    tbSpine.prototype.checkTexture = function () {
        var didLoad = true;
        if (Array.isArray(this.texture)) {
            for (var i = 0; i < this.texture.length; i++) {
                if (!this.texture[i].didLoad) {
                    didLoad = false;
                }
            }
        }
        else {
            didLoad = this.texture.didLoad;
        }
        return didLoad;
    };
    // Loading the skeleton
    tbSpine.prototype._loadSkeleton = function () {
        var _this = this;
        var isArray = false;
        this._textureFilenames = [];
        if (Array.isArray(this.texture)) {
            isArray = true;
            for (var i = 0; i < this.texture.length; i++) {
                if (!this.texture[i].didLoad || this.didLoad) {
                    return;
                }
            }
        }
        else {
            if (!this.texture.didLoad || this.didLoad) {
                return;
            }
        }
        var loader = new PIXI["loaders"]["Loader"]();
        var atlas = new PIXI["spine"]["core"]["TextureAtlas"]();
        if (isArray) {
            for (var i = 0; i < this.texture.length; i++) {
                var tex = this.texture[i];
                var filenames = [];
                for (var j = 0; j < this.texture[i].filenames.length; j++) {
                    filenames.push(this.texture[i].filenames[j]);
                }
                var collection = {
                    __tex: tex,
                    __filenames: filenames
                };
                this._textureFilenames.push(collection);
            }
        }
        else {
            this._textureFilenames.push({
                __tex: this.texture,
                __filenames: this.texture.filenames
            });
        }
        for (var i = 0; i < this._textureFilenames.length; i++) {
            for (var j = 0; j < this._textureFilenames[i].__filenames.length; j++) {
                atlas["addTextureHash"](this._textureFilenames[i].__tex.loader["resources"][this._textureFilenames[i].__filenames[j]]["textures"], true);
            }
        }
        loader.add(this.name, this.filePath, { "metadata": { "spineAtlas": atlas } }).load(function (yes, resources) {
            _this.spineData = resources[_this.name]["spineData"];
            _this.animation = new PIXI["spine"]["Spine"](_this.spineData);
            _this._initEvents();
            tbLoaderManager.loadedSpine();
        });
        this.didLoad = true;
    };
    // Clones the spine (such that you can use multiple at the same moment)
    tbSpine.prototype.clone = function () {
        var spine = new tbSpine(undefined, undefined, undefined, this);
        spine.animation = new PIXI["spine"]["Spine"](spine.spineParent.spineData);
        spine._initEvents();
        spine.setPosition(spine.x, spine.y);
        return spine;
    };
    return tbSpine;
}(tbBase));
export { tbSpine };
